<template>
  <div class="hotelux-search-bar" :class="{ sticky: isSticky }">
    <div class="search-bar-container">
      <div class="search-fields">
        <div class="search-field">
          <label class="field-label">Destination</label>
          <y-location
            :showlocation="true"
            :show-label="false"
            placeholder="Search destinations"
            :model-value="hotelSearchModel.location"
            :location-disabled="locationDisabled"
            @update:model-value="handleLocationChange"
          />
        </div>

        <div class="search-field">
          <label class="field-label">Check in / Check out</label>
          <y-datepicker
            :show-label="false"
            :show-night-count="!isSticky"
            v-model="searchModel.stay.date"
            date-format="MMM DD"
            @change="handleDateChange"
          >
            <template #icon>
              <font-icon
                class="icon-hotelux calendar-icon"
                icon="icon-hotelux-Calendar"
              />
            </template>
          </y-datepicker>
        </div>

        <div class="search-field">
          <label class="field-label">Rooms & Guests</label>
          <y-travelers
            :show-label="false"
            v-model="searchModel.stay.guest"
            @change="handleGuestChange"
          >
            <template #icon>
              <font-icon
                class="icon-hotelux calendar-icon"
                icon="icon-hotelux-Adults"
              />
            </template>
          </y-travelers>
        </div>
      </div>

      <button class="search-button" @click="handleSearch">
        <font-icon
          icon="icon-hotelux-Search"
          class="icon-hotelux search-icon"
        />
      </button>
    </div>
  </div>
</template>

<script setup>
import YDatepicker from "@packages/booking/hotel/Filter/Datepicker.vue";
import YLocation from "@packages/booking/hotel/Filter/Location.vue";
import YTravelers from "@packages/booking/hotel/Filter/Travelers.vue";
import dayjs from "dayjs";
import { ElMessage } from "element-plus";
import _ from "lodash";
import { storeToRefs } from "pinia";
import { useI18n } from "vue-i18n";

import { useHotelStoreHook } from "@/distributions/consumer/y/store/hotel";

const props = defineProps({
  showLabel: {
    type: Boolean,
    default: true
  },
  locationDisabled: {
    type: Boolean,
    default: false
  },
  locationRefIgnored: {
    type: Boolean,
    default: false
  },
  isSticky: {
    type: Boolean,
    default: false
  }
});

const emit = defineEmits(["search-change", "search-click"]);

const { t } = useI18n();
const hotelStore = useHotelStoreHook();
const { searchModel, hotelSearchModel } = storeToRefs(hotelStore);

const performSearch = () => {
  const locationRef = _.get(hotelSearchModel.value, "locationRef", "");
  if (locationRef || props.locationRefIgnored) {
    emit("search-change");
  }
};

const debouncedSearch = _.debounce(performSearch, 300);

const instantSearch = () => {
  debouncedSearch.cancel();
  const locationRef = _.get(hotelSearchModel.value, "locationRef", "");
  if (!locationRef && !props.locationRefIgnored) {
    ElMessage.error(t("shared.hotel.search.locationRequired"));
    return;
  }
  performSearch();
};
// const getDateFormat = () => {
//   const currentLang = locale.value;

//   // 判断语言组
//   const CJK_LANGUAGES = ["zh-TW", "zh-HK", "ko", "jp", "ja"];
//   const TH_LANGUAGE = "th";
//   const isCJK = CJK_LANGUAGES.includes(currentLang);
//   const isTH = currentLang === TH_LANGUAGE;

//   let format = "";
//   if (isCJK) {
//     format = "MM/DD ddd";
//   } else if (isTH) {
//     format = "DD MMM ddd";
//   } else {
//     format = "ddd, MMM DD";
//   }

//   return format;
// };
const handleLocationChange = newLocation => {
  hotelStore.hotelSearchModel.location = newLocation;

  if (newLocation) {
    hotelStore.hotelSearchModel.locationRef = newLocation._id;
    hotelStore.hotelSearchModel.locationType = newLocation.locationType;
  } else {
    hotelStore.hotelSearchModel.locationRef = "";
    hotelStore.hotelSearchModel.locationType = "";
    hotelStore.hotelSearchModel.filter = {};
    debouncedSearch();
    return;
  }

  const { locationType, _id, showLabel } = newLocation;
  let newFilter = {};

  switch (locationType) {
    case "city":
      newFilter = { city: { _id } };
      break;
    case "moduleTpHotel":
      newFilter = { hotels: [{ _id }] };
      hotelStore.selectedHotel = newLocation;
      break;
    case "googleMapPolitical":
      newFilter = {
        _biz: { googleMapPolitical: { place_id: _id, description: showLabel } }
      };
      break;
    case "search":
      newFilter = { search: showLabel };
      break;
  }
  hotelStore.hotelSearchModel.filter = newFilter;

  if (locationType === "moduleTpHotel") {
    instantSearch();
  } else {
    debouncedSearch();
  }
};

const handleDateChange = date => {
  hotelStore.searchModel.stay.date = {
    checkIn: dayjs(date[0]).format("YYYY-MM-DD"),
    checkOut: dayjs(date[1]).format("YYYY-MM-DD")
  };
  debouncedSearch();
};

const handleGuestChange = guest => {
  hotelStore.searchModel.stay.guest = guest;
  debouncedSearch();
};

const handleSearch = () => {
  const locationRef = _.get(hotelSearchModel.value, "locationRef", "");
  if (!locationRef && !props.locationRefIgnored) {
    ElMessage.error(t("shared.hotel.search.locationRequired"));
    return;
  }
  emit("search-click");
};

defineOptions({
  name: "HoteluxSearchBar"
});
</script>

<style lang="scss" scoped>
.hotelux-search-bar {
  width: 100%;
  display: flex;
  justify-content: center;
  padding: 0 max(60px, calc((100% - 1520px) / 2));

  &.sticky {
    padding: 0;

    .search-bar-container {
      width: 612px;
      height: 56px;
      background: #fff;
      border: 0;
      box-shadow: 0 2px 8px rgb(0 0 0 / 10%);
    }

    .search-fields {
      padding: 0 16px;
    }

    .search-field {
      width: auto;
      height: 24px;
      min-width: auto;
      padding: 0;
      margin: 0;
      border: 0;

      &:nth-child(1) {
        width: 98px;
        min-width: 98px;
      }

      &:nth-child(3) {
        width: 151px;
        min-width: 151px;
      }

      &:nth-child(5) {
        width: 246px;
      }

      &:not(:last-child) {
        border-right: 1px solid #d2d3d4;
        margin-right: 12px;
        padding-right: 12px;
      }
    }

    .field-label {
      display: none;
    }

    .field-divider {
      display: none;
    }

    .search-button {
      width: 32px;
      height: 32px;
      margin: 12px;
    }

    .search-icon {
      font-size: 12px;
    }

    .search-field {
      :deep(.location-selector) {
        width: 100%; // 占满父容器
        max-width: 100%;
        .el-input {
          width: 100%;
          max-width: 100%;
        }
        .el-input__wrapper {
          width: 100%;
          max-width: 100%;
          padding: 0;
          background: transparent !important;
          box-shadow: none;
          display: flex;
          align-items: center;
          overflow: hidden; // 隐藏溢出
        }
        .el-input__inner {
          width: 100%;
          max-width: 100%;
          height: 24px;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
          font-size: 16px;
          font-weight: 700;
          padding: 0;
          display: block;
          box-sizing: border-box;

          &::placeholder {
            color: #212223;
            opacity: 1;
            font-size: 16px;
            font-family: "lato-bold";
          }
        }
      }
      :deep(.y-datepicker) {
        .content-show {
          // width: 138px;
        }
      }
      :deep(.y-filter-travelers-selector) {
        .el-input__inner {
          font-size: 16px;
          font-weight: 700;
        }

        // 吸顶状态下拉面板灰色背景
        .el-select-dropdown {
          background: #f5f5f5;
        }

        .el-select-dropdown__item {
          background: #f5f5f5;

          &:hover {
            background: #e9e9e9;
          }
        }
      }
    }
  }
}

.search-bar-container {
  position: relative;
  display: flex;
  align-items: stretch;
  width: 960px;
  height: 90px;
  background: linear-gradient(90deg, #f4f4f4 0%, #f1f1f1 100%);
  border-image: linear-gradient(91deg, rgba(255, 255, 255, 1)) 1 1;
  border: 1px solid #ffffff;
  box-shadow: 0 4px 12px rgb(0 0 0 / 10%);
  backdrop-filter: blur(10px);
  transition: all 0.3s ease;
}

.search-fields {
  display: flex;
  align-items: center;
  flex: 1;
  padding: 0 40px;
  gap: 0;
}

.search-field {
  display: flex;
  flex-direction: column;
  justify-content: center;
  min-width: 227px;
  height: 50px;
  padding: 0;
  position: relative;

  &:not(:last-child) {
    border-right: 1px solid #e9e9e9;
    margin-right: 32px;
    padding-right: 32px;
  }
}

.field-label {
  font-family: "lato-bold";
  font-size: 10px;
  color: #757575;
  line-height: 18px;
  letter-spacing: 0;
  margin-bottom: 8px;
  letter-spacing: 2px;
  text-transform: uppercase;
}

.field-divider {
  width: 1px;
  height: 50px;
  background: #e9e9e9;
  margin: 0;
}

.search-button {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 66px;
  height: 66px;
  margin: 12px 12px 12px 0;
  background: #2a2c35;
  border: 0;
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover {
    background: #000;
  }
}

.search-icon {
  font-size: 24px;
  color: #fff;
}

:deep(.location-selector) {
  .el-input__wrapper {
    padding: 0;
    background: transparent !important;
    box-shadow: none;
    display: flex;
    align-items: center;
  }
  .el-input__prefix {
    width: 22px;
    height: 22px;
    margin: 2px 0 2px 2px;
    margin-right: 12px;
  }
  .search-icon {
    font-size: 20px;
  }
  .el-input__inner {
    font-family: Lato, -apple-system, sans-serif;
    font-size: 16px;
    font-weight: 400;
    color: #212223;
    line-height: 24px;
    padding: 0;
    height: 24px;
    border: 0;
    background: transparent !important;

    &::placeholder {
      color: #212223;
      opacity: 1;
      font-size: 16px;
      font-family: "lato-bold";
    }
  }
  .el-input__prefix-inner > :last-child {
    margin-right: 12px;
  }
}
:deep(.y-datepicker) {
  // min-width: 227px;
  // width: 230px;
  .label {
    display: none;
  }

  .el-input {
    height: 24px;
  }

  .el-input__wrapper {
    padding: 0;
    box-shadow: none;
    background: transparent;
  }
  .content-show .text-date {
    font-size: 16px;
    font-family: "lato-bold";
  }
  .text-night {
    background: #e9e9e9;
    padding: 4px 8px;
    font-size: 12px;
    color: #212223;
    font-family: "lato";
  }
  .el-input__inner {
    font-family: Lato, -apple-system, sans-serif;
    font-size: 16px;
    font-weight: 400;
    color: #212223;
    line-height: 24px;
    padding: 0;
    height: 24px;
    border: 0;
    background: transparent;
  }

  .el-input__prefix {
    margin-right: 12px;
  }

  .el-icon {
    font-size: 20px;
    color: #2a2c35;
  }

  .nights-badge {
    display: inline-block;
    padding: 4px 8px;
    margin-left: 8px;
    font-family: Lato, -apple-system, sans-serif;
    font-size: 12px;
    font-weight: 400;
    color: #212223;
    line-height: 16px;
    background: #e9e9e9;
  }
}

:deep(.y-filter-travelers-selector) {
  .label {
    display: none;
  }
  .text-info {
    font-family: "lato-bold";
    font-size: 16px;
    font-weight: 400;
    color: #212223;
    line-height: 24px;
  }
}

.calendar-icon {
  margin-right: 12px;
  font-size: 18px;
}
</style>
